import { Injectable } from '@angular/core';
import { buildFileDiff, FileDiff, streamingLineDiff } from './coding-diff';
import { CodingNote } from './coding-answer.model';

export interface DiffLine {
  type: 'add' | 'remove' | 'unchanged';
  content: string;
  lineNumber?: number;
}

@Injectable({
  providedIn: 'root'
})
export class DiffService {
  /**
   * The diff of a reply that is still streaming in: only what has been written so far, matched from the top.
   * See {@link streamingLineDiff}.
   */
  generateStreamingFileDiff(oldText: string | undefined, partialNewText: string): FileDiff {
    return buildFileDiff(streamingLineDiff(oldText, partialNewText), [], oldText === undefined);
  }

  /**
   * Builds the grouped, explained diff used by the coding workspace. `undefined` means the file
   * does not exist on that side (new or deleted file), which differs from an empty file.
   */
  generateFileDiff(oldText: string | undefined, newText: string | undefined, notes: CodingNote[] = []): FileDiff {
    return buildFileDiff(this.computeDiff(
      oldText === undefined ? [] : oldText.split('\n'),
      newText === undefined ? [] : newText.split('\n')
    ), notes, oldText === undefined);
  }
  
  /**
   * Compares two strings line by line and returns diff information
   * Uses a simple LCS-based diff algorithm
   */
  generateDiff(oldText: string, newText: string): DiffLine[] {
    const oldLines = oldText.split('\n');
    const newLines = newText.split('\n');
    
    return this.computeDiff(oldLines, newLines);
  }

  /**
   * Simple line-by-line diff using LCS (Longest Common Subsequence)
   */
  private computeDiff(oldLines: string[], newLines: string[]): DiffLine[] {
    const result: DiffLine[] = [];
    const m = oldLines.length;
    const n = newLines.length;
    
    // Build LCS matrix
    const lcs: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
    
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (oldLines[i - 1] === newLines[j - 1]) {
          lcs[i][j] = lcs[i - 1][j - 1] + 1;
        } else {
          lcs[i][j] = Math.max(lcs[i - 1][j], lcs[i][j - 1]);
        }
      }
    }
    
    // Backtrack to build diff
    let i = m, j = n;
    const diffStack: DiffLine[] = [];
    
    while (i > 0 || j > 0) {
      if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
        diffStack.push({ type: 'unchanged', content: oldLines[i - 1] });
        i--;
        j--;
      } else if (j > 0 && (i === 0 || lcs[i][j - 1] >= lcs[i - 1][j])) {
        diffStack.push({ type: 'add', content: newLines[j - 1] });
        j--;
      } else if (i > 0) {
        diffStack.push({ type: 'remove', content: oldLines[i - 1] });
        i--;
      }
    }
    
    return diffStack.reverse();
  }

  /**
   * Checks if two texts are similar enough to show as diff
   * (e.g., more than 30% similarity)
   */
  shouldShowDiff(oldText: string, newText: string): boolean {
    const oldLines = oldText.split('\n');
    const newLines = newText.split('\n');
    
    // Don't show diff if texts are too different in length
    const lengthRatio = Math.min(oldLines.length, newLines.length) / Math.max(oldLines.length, newLines.length);
    if (lengthRatio < 0.3) return false;
    
    // Calculate similarity based on common lines
    const commonLines = this.countCommonLines(oldLines, newLines);
    const similarity = commonLines / Math.max(oldLines.length, newLines.length);
    
    return similarity >= 0.3;
  }

  private countCommonLines(oldLines: string[], newLines: string[]): number {
    const oldSet = new Set(oldLines);
    return newLines.filter(line => oldSet.has(line)).length;
  }

  /**
   * Format diff lines as HTML with proper markers and classes
   */
  formatDiffAsHtml(diffLines: DiffLine[]): string {
    return diffLines.map(line => {
      const escapedContent = this.escapeHtml(line.content);
      switch (line.type) {
        case 'add':
          return `<div class="ln-add"><span class="diff-marker">++ </span>${escapedContent}</div>`;
        case 'remove':
          return `<div class="ln-del"><span class="diff-marker">-- </span>${escapedContent}</div>`;
        default:
          return `<div class="ln-unchanged">${escapedContent}</div>`;
      }
    }).join('');
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
